import ExcelJS from 'exceljs';
import { format } from 'date-fns';
import type {
  NewAccountsReportItem,
  PaysReportItem,
  ReceivedPaysReportItem,
} from '@/models/report/types';
import {
  buildNewClientsExportData,
  buildPaysExportData,
  buildReceivedPaysExportData,
  type CountryOf,
  type ExportRow,
  type ReportExportData,
} from '@/lib/reportExportData';

const HEADER_FILL: ExcelJS.Fill = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FF342E37' },
};
const HEADER_FONT: Partial<ExcelJS.Font> = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
const ROW_ODD: ExcelJS.Fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFAFFFD' } };
const ROW_EVEN: ExcelJS.Fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0EEF2' } };
const MONEY_FMT = '#,##0.00';

function autoWidth(sheet: ExcelJS.Worksheet) {
  sheet.columns.forEach((col) => {
    let max = 12;
    col.eachCell?.({ includeEmpty: false }, (cell) => {
      const len = String(cell.value ?? '').length;
      if (len > max) max = len;
    });
    col.width = Math.min(max + 2, 40);
  });
}

function styleHeader(sheet: ExcelJS.Worksheet) {
  const header = sheet.getRow(1);
  header.eachCell((cell) => {
    cell.fill = HEADER_FILL;
    cell.font = HEADER_FONT;
    cell.alignment = { vertical: 'middle', horizontal: 'left' };
  });
  header.height = 20;
  sheet.views = [{ state: 'frozen', ySplit: 1 }];
}

function styleRows(sheet: ExcelJS.Worksheet) {
  sheet.eachRow((row, i) => {
    if (i === 1) return;
    row.eachCell((cell) => {
      cell.fill = i % 2 === 0 ? ROW_EVEN : ROW_ODD;
    });
  });
}

async function download(wb: ExcelJS.Workbook, name: string) {
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${name}_${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}

async function exportReportToExcel(report: ReportExportData) {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet(report.sheetName);

  ws.columns = report.columns.map((column) => ({
    header: column.header,
    key: column.key,
  }));

  const addRow = (item: ExportRow) => {
    const row = ws.addRow(item);
    report.columns.forEach((column) => {
      if (column.type === 'money') {
        row.getCell(column.key).numFmt = MONEY_FMT;
      }
    });
    return row;
  };

  report.rows.forEach(addRow);
  styleHeader(ws);
  styleRows(ws);

  if (report.totals.length > 0) {
    ws.addRow([]);
    report.totals.forEach((item) => {
      addRow(item).font = { bold: true };
    });
  }

  autoWidth(ws);
  await download(wb, report.fileName);
}

export async function exportNewClientsToExcel(
  items: NewAccountsReportItem[],
  countryOf: CountryOf<NewAccountsReportItem>
) {
  await exportReportToExcel(buildNewClientsExportData(items, countryOf));
}

export async function exportPaysToExcel(
  items: PaysReportItem[],
  countryOf: CountryOf<PaysReportItem>
) {
  await exportReportToExcel(buildPaysExportData(items, countryOf));
}

export async function exportReceivedPaysToExcel(
  items: ReceivedPaysReportItem[],
  countryOf: CountryOf<ReceivedPaysReportItem>
) {
  await exportReportToExcel(buildReceivedPaysExportData(items, countryOf));
}
